/**
 * Orynvia scroll reveal.
 * Fades content blocks up with a soft ease as they scroll into view, staggering
 * blocks that enter together. Content already on screen at load is left alone
 * (no flicker), and the effect is skipped in the theme editor and for visitors
 * who prefer reduced motion.
 */
(() => {
  if (window.Shopify?.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Leaf-level blocks worth animating. Containers (groups, sections) are left static.
  const TARGETS = [
    '.text-block',
    '.price',
    '.variant-picker',
    '.buy-buttons-block',
    '.button',
    '.button-unstyled',
    '.product-information__media',
    '.image-block',
    '.icon-block',
    '.product-card',
    '.collection-card',
    '.card',
    '.accordion',
    '.email-signup-block',
    '.rating-wrapper',
    '.marquee__wrapper',
    '.orynvia-catalog__card',
    '.orynvia-catalog__banner',
    '.orynvia-catalog__trust li',
  ].join(',');

  const MEDIA =
    '.product-information__media, .image-block, .product-card, .collection-card, .card, .orynvia-catalog__card, .orynvia-catalog__banner';
  const STAGGER_MS = 130;
  const MAX_STAGGER_STEPS = 5;

  const pending = new Set();

  // Reveal a batch of elements, staggering those that appear together (top-to-bottom, left-to-right).
  const reveal = (elements) => {
    elements
      .sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        return ra.top - rb.top || ra.left - rb.left;
      })
      .forEach((el, index) => {
        if (!pending.delete(el)) return;
        observer.unobserve(el);
        el.style.setProperty('--reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
        el.classList.add('is-revealed');
        // Drop the helper classes once the fade-up finishes so the block's own styles take over again.
        const cleanUp = (event) => {
          if (event.target !== el || event.propertyName !== 'transform') return;
          el.classList.remove('reveal', 'reveal--media', 'is-revealed');
          el.removeEventListener('transitionend', cleanUp);
        };
        el.addEventListener('transitionend', cleanUp);
      });
  };

  const observer = new IntersectionObserver(
    (entries) => reveal(entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target)),
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
  );

  // Safety net for jumps (anchor links, fast flicks): anything that has reached or passed the
  // viewport without the observer firing is revealed too, so nothing is ever left invisible.
  let scrollQueued = false;
  const revealPassed = () => {
    scrollQueued = false;
    reveal([...pending].filter((el) => el.getBoundingClientRect().top < window.innerHeight));
  };
  const onScroll = () => {
    if (scrollQueued || pending.size === 0) return;
    scrollQueued = true;
    requestAnimationFrame(revealPassed);
  };
  // Capture phase catches scrolling on Horizon's desktop scroll container (.page-wrapper) as well as the window.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('hashchange', () => setTimeout(revealPassed, 50));

  const isInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  const prepare = (root) => {
    root.querySelectorAll(TARGETS).forEach((el) => {
      // Only animate the outermost target; nested targets move with their parent.
      if (el.parentElement?.closest(TARGETS)) return;
      // The hero and the catalog's first row have their own on-load animation.
      if (el.closest('.hero, [data-load-reveal]')) return;
      if (isInViewport(el)) return;

      el.classList.add('reveal');
      if (el.matches(MEDIA)) el.classList.add('reveal--media');
      pending.add(el);
      observer.observe(el);
    });
  };

  const init = () => {
    document.querySelectorAll('main .shopify-section, .shopify-section-group-footer-group').forEach(prepare);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
